import React from 'react';
import { Box, Typography, Grid, useTheme, alpha, Chip, Stack } from '@mui/material';
import { 
  AutoAwesome as SparkleIcon,
  TrendingUp as TrendingIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Shield as ShieldIcon,
  Factory as ManufacturingIcon,
  Analytics as AnalyticsIcon,
  Palette as BeautyIcon
} from '@mui/icons-material';

const GridText = () => {
  const theme = useTheme();

  const features = [
    {
      title: 'Complete Business Management',
      description: 'Streamline your beauty business operations with comprehensive financial tracking, inventory management, and production control.',
      icon: <BusinessIcon />
    },
    {
      title: 'Modern Technology Stack',
      description: 'Built with React, Material-UI, and Supabase for real-time updates, mobile responsiveness, and enterprise-grade security.',
      icon: <CodeIcon />
    },
    {
      title: 'Multi-Tenant Security',
      description: 'Row Level Security ensures complete data isolation between companies with JWT authentication and role-based access control.',
      icon: <ShieldIcon />
    },
    {
      title: 'Advanced Manufacturing',
      description: 'Dual production systems for batch and line production with quality control integration, cost analysis, and yield tracking.',
      icon: <ManufacturingIcon />
    },
    {
      title: 'Comprehensive Analytics',
      description: 'Real-time business insights with sales dashboards, production metrics, inventory analytics, and performance tracking.',
      icon: <AnalyticsIcon />
    },
    {
      title: 'Built for Beauty Industry',
      description: 'Empowering beauty brands to focus on what they do best - creating amazing products while we handle the operations.',
      icon: <BeautyIcon />
    }
  ];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 8 }, 
      px: { xs: 2, sm: 3 },
      bgcolor: alpha(theme.palette.grey[50], 0.5)
    }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          {/* Header Badge */}
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip 
              icon={<SparkleIcon sx={{ fontSize: { xs: 18, md: 20 } }} />}
              label="Enterprise Grade Platform"
              variant="outlined"
              sx={{ 
                borderColor: 'primary.main',
                color: 'primary.main',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                '& .MuiChip-icon': {
                  color: 'primary.main'
                }
              }}
            />
          </Stack>

          {/* Main Title with Gradient */}
          <Typography variant="h3" sx={{ 
            fontWeight: 'normal', 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: { xs: 2, md: 3 },
            fontSize: { xs: '1.75rem', sm: '2.2rem', md: '2.8rem' },
            letterSpacing: '-0.02em',
            px: { xs: 1, md: 0 }
          }}>
            The Complete Beauty Business Platform
          </Typography>

          {/* Subtitle with enhanced styling */}
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            maxWidth: 650,
            mx: 'auto',
            lineHeight: 1.7,
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
            mb: { xs: 3, md: 4 },
            px: { xs: 2, md: 0 }
          }}>
            Everything you need to run your beauty business efficiently, from formulation to final sale
          </Typography>

          {/* Feature Highlights */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1.5, sm: 2 }} 
            justifyContent="center"
            alignItems="center"
            sx={{ px: { xs: 2, sm: 0 } }}
          >
            <Chip 
              icon={<TrendingIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
              label="Real-time Analytics"
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
                width: { xs: '100%', sm: 'auto' }
              }}
            />
            <Chip 
              icon={<SecurityIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
              label="Enterprise Security"
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: 'info.main',
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
                width: { xs: '100%', sm: 'auto' }
              }}
            />
            <Chip 
              label="Multi-Tenant Ready"
              size="small"
              sx={{ 
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                fontSize: { xs: '0.75rem', md: '0.8125rem' },
                width: { xs: '100%', sm: 'auto' }
              }}
            />
          </Stack>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={index}>
              <Box sx={{ 
                p: { xs: 3, md: 4 },
                height: '100%',
                borderRadius: { xs: 2, md: 3 },
                bgcolor: 'white',
                border: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.05)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.3)
                }
              }}>
                {/* Icon and Title */}
                <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 1.5, md: 2 } }}>
                  <Box sx={{
                    p: { xs: 1.25, md: 1.5 },
                    borderRadius: { xs: 1.5, md: 2 },
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': {
                      fontSize: { xs: 20, md: 24 }
                    }
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: 'text.primary',
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}>
                    {feature.title}
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.9rem', md: '0.95rem' }
                }}>
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 }, px: { xs: 2, md: 0 } }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 500, 
            color: 'primary.main',
            mb: { xs: 1.5, md: 2 },
            fontStyle: 'italic',
            fontSize: { xs: '1.2rem', md: '1.5rem' }
          }}>
            Built with love for the Beauty Industry
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}>
            Empowering beauty brands to focus on what they do best - creating amazing products
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GridText;
